import React, { useEffect, useRef, useState } from 'react';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import { Download, ExternalLink, Printer, QrCode, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { getInspection } from '../../../api/inspection';
import {
  MELBOURNE_TZ,
  formatMelbourneClock,
  formatMelbourneDayLabel,
} from '../../../utils/melbourneTime';
import { buildOpenHomePrintHtml } from './openHomePrintTemplate';

/**
 * Reusable "QR Link to Portal" modal for an inspection / open home.
 *
 * The QR encodes the property's PUBLIC buyer-portal listing link so buyers who
 * scan the open-home signage land straight on the listing on the portal. We
 * prefer the short, address-aware public share link
 * (https://portal.collings.com.au/p/<slug>) and fall back to the authed
 * /property/:id route only if the property has no share slug.
 *
 * Pass either a full ``inspection`` object or just an ``inspectionId`` — the
 * modal fetches the inspection on open so we always have the portal slug even
 * from the list view.
 */
const BUYER_PORTAL_BASE =
  process.env.REACT_APP_BUYER_PORTAL_URL || 'https://portal.collings.com.au';

export default function OpenHomeQrModal({ inspection: initial, inspectionId, onClose }) {
  const [inspection, setInspection] = useState(initial || null);
  const [loading, setLoading] = useState(false);
  const canvasRef = useRef(null);

  // Refetch when we have an id but not yet the portal link data (share slug or
  // property id), e.g. when the row passed in came from a stale list cache.
  useEffect(() => {
    const id = inspectionId || initial?.id;
    if (!id) return;
    if (inspection && (inspection.property_share_slug || inspection.property)) return;
    setLoading(true);
    getInspection(id)
      .then((res) => setInspection(res.data))
      .catch(() => toast.error('Could not load this open home.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectionId, initial?.id]);

  // Build the public portal listing URL the QR points to.
  const portalUrl = inspection
    ? (inspection.property_share_slug
        ? `${BUYER_PORTAL_BASE}/p/${inspection.property_share_slug}`
        : (inspection.property
            ? `${BUYER_PORTAL_BASE}/property/${inspection.property}`
            : null))
    : null;
  const printSvgRef = useRef(null);

  const downloadPng = () => {
    const canvas = canvasRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `open-home-${inspection.id}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const copyUrl = async () => {
    if (!portalUrl) return;
    try {
      await navigator.clipboard.writeText(portalUrl);
      toast.success('Portal link copied.');
    } catch {
      toast.error('Could not copy.');
    }
  };

  const printSheet = () => {
    if (!inspection || !portalUrl) return;
    const svgEl = printSvgRef.current?.querySelector('svg');
    const qrSvgMarkup = svgEl
      ? svgEl.outerHTML.replace('<svg ', '<svg xmlns="http://www.w3.org/2000/svg" ')
      : '';
    // Printed buyer flyer — always Melbourne wall-clock, never the operator's
    // browser timezone. Keep the long human-readable date format.
    const dateStr = inspection.scheduled_date
      ? new Intl.DateTimeFormat('en-AU', {
          timeZone: MELBOURNE_TZ,
          weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
        }).format(new Date(inspection.scheduled_date))
      : '';
    const timeStr = inspection.scheduled_date
      ? `${formatMelbourneClock(inspection.scheduled_date)} – ${formatMelbourneClock(new Date(new Date(inspection.scheduled_date).getTime() + (inspection.duration_minutes || 30) * 60000))}`
      : '';
    const html = buildOpenHomePrintHtml({
      logoUrl: `${window.location.origin}/collingslogo-removebg-preview.png`,
      propertyAddress: inspection.property_address || inspection.property_title || '',
      dateStr,
      timeStr,
      saleMethodLabel: prettySaleMethod(inspection.sale_method),
      portalUrl,
      qrSvgMarkup,
    });
    const w = window.open('', '_blank', 'width=900,height=1100');
    if (!w) {
      toast.error('Pop-up blocked — allow pop-ups to print the QR sheet.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    // Wait for the logo image to load before firing print, otherwise it's blank.
    const trigger = () => { try { w.focus(); w.print(); } catch (_) {} };
    const img = w.document.querySelector('img.logo');
    if (img && !img.complete) {
      img.addEventListener('load', trigger);
      img.addEventListener('error', trigger);
    } else {
      setTimeout(trigger, 250);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-base font-semibold text-gray-900">QR Link to Portal</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {inspection?.property_address || inspection?.property_title || (loading ? 'Loading…' : '')}
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="flex-shrink-0 -mr-1 p-1 text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-6 space-y-4">
          {inspection && (
            <div className="grid grid-cols-3 gap-2 text-[11px] bg-slate-50 rounded-lg px-3 py-2">
              <DetailCell label="Date" value={inspection.scheduled_date ? formatMelbourneDayLabel(inspection.scheduled_date) : '—'} />
              <DetailCell label="Time" value={inspection.scheduled_date ? formatMelbourneClock(inspection.scheduled_date) : '—'} />
              <DetailCell label="Duration" value={`${inspection.duration_minutes || 30} min`} />
              <DetailCell label="Sale method" value={prettySaleMethod(inspection.sale_method)} />
              <DetailCell label="Checked in" value={inspection.total_attendees ?? 0} />
              <DetailCell label="Status" value={inspection.status || '—'} />
            </div>
          )}

          <div className="flex flex-col items-center bg-gray-50 rounded-xl p-5">
            {portalUrl ? (
              <div ref={canvasRef} className="bg-white p-4 rounded-lg border">
                <QRCodeCanvas value={portalUrl} size={220} level="M" includeMargin={false} />
              </div>
            ) : (
              <div className="w-56 h-56 flex items-center justify-center bg-white border rounded-lg text-xs text-gray-400">
                <QrCode className="w-12 h-12" />
              </div>
            )}
            <div className="mt-3 text-[11px] text-gray-500 break-all text-center max-w-xs">{portalUrl}</div>
            <div className="mt-4 flex gap-2 flex-wrap justify-center">
              {portalUrl && (
                <>
                  <button type="button" onClick={printSheet}
                    className="text-xs flex items-center gap-1 px-3 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800">
                    <Printer className="w-3.5 h-3.5" /> Print / Save as PDF
                  </button>
                  <button type="button" onClick={downloadPng}
                    className="text-xs flex items-center gap-1 px-3 py-2 border rounded-md bg-white hover:bg-slate-50">
                    <Download className="w-3.5 h-3.5" /> Download PNG
                  </button>
                  <button type="button" onClick={copyUrl}
                    className="text-xs flex items-center gap-1 px-3 py-2 border rounded-md bg-white hover:bg-slate-50">
                    Copy link
                  </button>
                  <a href={portalUrl} target="_blank" rel="noreferrer"
                     className="text-xs flex items-center gap-1 px-3 py-2 border rounded-md bg-white hover:bg-slate-50">
                    <ExternalLink className="w-3.5 h-3.5" /> Open listing
                  </a>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Off-screen SVG render — printSheet() pulls the markup out of this. */}
        {portalUrl && (
          <div ref={printSvgRef} style={{ position: 'absolute', left: -9999, top: -9999 }} aria-hidden>
            <QRCodeSVG value={portalUrl} size={640} level="M" includeMargin={false} />
          </div>
        )}
      </div>
    </div>
  );
}

function DetailCell({ label, value }) {
  return (
    <div>
      <div className="text-slate-400 uppercase tracking-wide text-[9px]">{label}</div>
      <div className="text-slate-800 font-medium">{value}</div>
    </div>
  );
}

function prettySaleMethod(m) {
  switch (m) {
    case 'private_sale': return 'Private sale';
    case 'auction':      return 'Auction';
    case 'eoi':          return 'EOI';
    case 'off_market':   return 'Off-market';
    default:             return m || '—';
  }
}
